/**
 * CL-PROPERTY-OS-002C3 / 002C3B1 — Client-safe Property OS callable result normalization.
 */

function safeStr(value) {
  return String(value ?? "").trim();
}

export const PROPERTY_OS_RESULT_CODES = Object.freeze({
  CONVERTED: "converted",
  ALREADY_COMPLETE: "already_complete",
  NOT_APPLICABLE: "not_applicable",
});

export const PROPERTY_OS_CLIENT_OUTCOMES = Object.freeze({
  SUCCESS: "success",
  NOT_APPLICABLE: "not_applicable",
  RETRYABLE_FAILURE: "retryable_failure",
  FAILURE: "failure",
});

const RECOGNIZED_RESULT_CODES = new Set(Object.values(PROPERTY_OS_RESULT_CODES));

const GENERIC_INVITATION_FAILURE =
  "We could not finish setting up your property portal access. Please try again or contact support.";
const GENERIC_CONVERSION_FAILURE =
  "We could not finish updating your property portal access. Please try again or contact support.";
const EMAIL_NOT_VERIFIED_MESSAGE = "Verify your email address before continuing.";
const GENERIC_AUTH_MESSAGE = "Sign in to continue.";

function isNonNegativeInteger(value) {
  return Number.isInteger(value) && value >= 0;
}

export function validatePropertyOsCallableSuccessPayload(data) {
  if (!data || typeof data !== "object") {
    return { ok: false, reason: "malformed_payload" };
  }
  if (data.ok !== true) {
    return { ok: false, reason: "callable_not_ok" };
  }
  const resultCode = safeStr(data.resultCode);
  if (!resultCode) {
    return { ok: false, reason: "missing_result_code" };
  }
  if (!RECOGNIZED_RESULT_CODES.has(resultCode)) {
    return { ok: false, reason: "unknown_result_code" };
  }
  const convertedCount = Number(data.convertedCount);
  if (!isNonNegativeInteger(convertedCount)) {
    return { ok: false, reason: "invalid_converted_count" };
  }
  const alreadyCompleteCount = Number(data.alreadyCompleteCount);
  if (!isNonNegativeInteger(alreadyCompleteCount)) {
    return { ok: false, reason: "invalid_already_complete_count" };
  }
  if (typeof data.inviteConsumed !== "boolean") {
    return { ok: false, reason: "invalid_invite_consumed" };
  }
  return {
    ok: true,
    normalized: {
      ok: true,
      resultCode,
      convertedCount,
      alreadyCompleteCount,
      inviteConsumed: data.inviteConsumed,
    },
  };
}

export function normalizePropertyOsCallableSuccess(data = {}) {
  const validation = validatePropertyOsCallableSuccessPayload(data);
  if (!validation.ok) {
    return {
      ok: false,
      convertedCount: 0,
      alreadyCompleteCount: 0,
      inviteConsumed: false,
      resultCode: "",
      validationError: validation.reason,
    };
  }
  return validation.normalized;
}

export function mapPropertyOsCallableErrorToClientMessage(error, { invitation = false } = {}) {
  const code = safeStr(error?.code);
  const message = safeStr(error?.message);
  if (code === "unauthenticated") return GENERIC_AUTH_MESSAGE;
  if (code === "failed-precondition" && /verify your email/i.test(message)) {
    return EMAIL_NOT_VERIFIED_MESSAGE;
  }
  if (invitation) return GENERIC_INVITATION_FAILURE;
  return GENERIC_CONVERSION_FAILURE;
}

export function classifyPropertyOsCallableSuccess(result = {}) {
  const validation = validatePropertyOsCallableSuccessPayload(result);
  if (!validation.ok) {
    return {
      outcome: PROPERTY_OS_CLIENT_OUTCOMES.FAILURE,
      result: null,
      validationError: validation.reason,
    };
  }
  const normalized = validation.normalized;
  if (normalized.resultCode === PROPERTY_OS_RESULT_CODES.NOT_APPLICABLE) {
    return { outcome: PROPERTY_OS_CLIENT_OUTCOMES.NOT_APPLICABLE, result: normalized };
  }
  return { outcome: PROPERTY_OS_CLIENT_OUTCOMES.SUCCESS, result: normalized };
}

export function isPropertyOsNotApplicableResult(result = {}) {
  const validation = validatePropertyOsCallableSuccessPayload(result);
  return validation.ok && validation.normalized.resultCode === PROPERTY_OS_RESULT_CODES.NOT_APPLICABLE;
}

export function isTrustedCompletionSuccessOutcome(trustedResult) {
  const outcome = trustedResult?.outcome;
  return (
    outcome === PROPERTY_OS_CLIENT_OUTCOMES.SUCCESS || outcome === PROPERTY_OS_CLIENT_OUTCOMES.NOT_APPLICABLE
  );
}

/** Portal reload is allowed only after validated trusted SUCCESS or NOT_APPLICABLE. */
export function trustedOutcomeAllowsPortalReload(trustedResult) {
  if (trustedResult?.skipped === true) return false;
  return isTrustedCompletionSuccessOutcome(trustedResult);
}

export function interpretTrustedLifecycleCompletion({ lifecycleOk, trustedResult } = {}) {
  if (!lifecycleOk) {
    return { ok: false, retryable: true };
  }
  if (!trustedOutcomeAllowsPortalReload(trustedResult)) {
    return {
      ok: false,
      retryable: true,
      clientMessage: trustedResult?.error?.clientMessage,
      failedStep: "trusted_property_os_completion",
    };
  }
  if (trustedResult?.outcome === PROPERTY_OS_CLIENT_OUTCOMES.NOT_APPLICABLE) {
    return { ok: true, notApplicable: true };
  }
  return { ok: true };
}

/**
 * Returns true when the conversion callable error represents a "not applicable"
 * condition (no invite and no reciprocal CRM link) rather than a genuine failure.
 * These errors mean the account simply has no prepared Property OS memberships
 * to convert — they are not retryable and should be silent no-ops.
 * @param {unknown} error
 */
export function isConversionNotApplicableClientError(error) {
  const code = safeStr(error?.code).toLowerCase();
  if (code !== "functions/failed-precondition") return false;
  const message = safeStr(error?.message).toLowerCase();
  // Server sends IDENTITY_UNAVAILABLE for identity_conflict and
  // invite_or_reciprocal_link_required (both IDENTITY_CONFLICT error code).
  return (
    message.includes("property access is not available for this account") ||
    message.includes("identity_conflict") ||
    message.includes("invite_or_reciprocal_link_required")
  );
}

export async function invokePropertyOsCallableSafely(callFn, { invitation = false } = {}) {
  try {
    const data = await callFn();
    const classified = classifyPropertyOsCallableSuccess(data);
    return { ...classified, error: null };
  } catch (error) {
    if (!invitation && isConversionNotApplicableClientError(error)) {
      return {
        outcome: PROPERTY_OS_CLIENT_OUTCOMES.NOT_APPLICABLE,
        result: null,
        error: null,
      };
    }
    return {
      outcome: PROPERTY_OS_CLIENT_OUTCOMES.RETRYABLE_FAILURE,
      result: null,
      error: {
        clientMessage: mapPropertyOsCallableErrorToClientMessage(error, { invitation }),
        code: safeStr(error?.code),
      },
    };
  }
}
